/**
 * The roster calls: workers and their roles, EPF membership, plants, the
 * EPF/ETF rates and the misc categories. Owner-only on the server (plants also
 * open to the `plants` permission). Settings uses the workers, rates and
 * categories; the Permits tab uses the plants.
 *
 * Live only. These are Settings edits the owner makes from his phone, not the
 * clerk's day entry, so they are not queued in the outbox — with no signal the
 * screen says so and keeps the form.
 */

import { request } from './api';

export interface RoleRow {
  id: string;
  name: string;
  baseRs: number;
  addonRs: number;
  dailyRateRs: number;
}

/** How he is paid: which pay run his days go to, and how his loans repay (migration 0015). */
export type PayCycle = 'weekly' | 'monthly';

export interface EpfPeriod {
  epfNo: string;
  memberFrom: string;
  stoppedOn: string | null;
  /** Sent with a member: his EPF salary — a week's for a weekly man, a month's for a monthly one. */
  salaryRs?: number | null;
}

export interface MembershipChange {
  at: string;
  by: string | null;
  what: string;
}

export interface WorkerRow {
  id: string;
  name: string;
  isActive: boolean;
  sortOrder: number;
  rowVersion: number;
  roles: RoleRow[];
  payCycle: PayCycle;
  /** His usual cost code: where his row on the time sheet starts. */
  costCodeId: string | null;
  /** The EPF salary EPF/ETF is capped at, per week or per month, or null. */
  epfSalaryRs: number | null;
  /** The period in force today, or null. */
  epf: EpfPeriod | null;
  /** The most recent period even if it has ended — "ended 4 Sept". */
  lastMembership: EpfPeriod | null;
  memberships: (EpfPeriod & { id: string })[];
  membershipChanges: MembershipChange[];
}

/** What the editor sends. Roles keep their ids; a new row has none. */
export interface RoleInput {
  id?: string | null;
  name: string;
  baseRs: number;
  addonRs: number;
}

export interface WorkerInput {
  name: string;
  roles: RoleInput[];
  payCycle: PayCycle;
  /** null: not a member (an open period is stopped today). */
  epf: EpfPeriod | null;
  costCodeId?: string | null;
  isActive?: boolean;
  rowVersion?: number;
}

/** A road vehicle's papers: each is an expiry date, or null when it does not apply. */
export interface PlantPapers {
  isRoadVehicle: boolean;
  registrationNo: string | null;
  licenceExpiresOn: string | null;
  insuranceExpiresOn: string | null;
  emissionExpiresOn: string | null;
  fitnessExpiresOn: string | null;
}

export interface PlantRow extends PlantPapers {
  /** Next service due — any plant, road vehicle or not; null when none is set. */
  serviceDueOn: string | null;
  id: string;
  code: string;
  name: string;
  isActive: boolean;
  sortOrder: number;
  rowVersion: number;
  /** Its usual cost code: where its fuel and misc start each day. */
  costCodeId: string | null;
}

export type PlantInput = { code: string; name: string; costCodeId?: string | null; serviceDueOn?: string | null } & Partial<PlantPapers>;

export interface RatesRow {
  id: string;
  effectiveFrom: string;
  epfEmployeePctX100: number;
  epfEmployerPctX100: number;
  etfPctX100: number;
  createdAt?: string;
}

export interface CategoryRow {
  id: string;
  name: string;
  requiresPlant: boolean;
  isActive: boolean;
  sortOrder: number;
  rowVersion: number;
}

export const rosterApi = {
  listWorkers: () => request<{ workers: WorkerRow[] }>('/workers'),

  createWorker: (body: WorkerInput) =>
    request<WorkerRow>('/workers', { method: 'POST', body }),

  updateWorker: (id: string, body: Partial<WorkerInput>) =>
    request<WorkerRow>(`/workers/${id}`, { method: 'PATCH', body }),

  listPlants: () => request<{ plants: PlantRow[] }>('/plants'),

  createPlant: (body: PlantInput) =>
    request<PlantRow>('/plants', { method: 'POST', body }),

  updatePlant: (id: string, body: Partial<PlantInput> & { isActive?: boolean; rowVersion?: number }) =>
    request<PlantRow>(`/plants/${id}`, { method: 'PATCH', body }),

  removePlant: (id: string) => request<void>(`/plants/${id}`, { method: 'DELETE' }),

  rates: () => request<{ today: string; current: RatesRow | null; history: RatesRow[]; payDay: number }>('/rates'),

  /** The day of the month EPF/ETF members are paid, for the month before. */
  setPayDay: (day: number) => request<{ payDay: number }>('/rates/pay-day', { method: 'POST', body: { day } }),

  saveRates: (body: {
    effectiveFrom?: string;
    epfEmployeePctX100: number;
    epfEmployerPctX100: number;
    etfPctX100: number;
  }) => request<RatesRow>('/rates', { method: 'POST', body }),

  listCategories: () => request<{ categories: CategoryRow[] }>('/misc-categories'),

  createCategory: (body: { name: string; requiresPlant: boolean }) =>
    request<CategoryRow>('/misc-categories', { method: 'POST', body }),

  updateCategory: (
    id: string,
    body: { name?: string; requiresPlant?: boolean; isActive?: boolean; rowVersion?: number },
  ) => request<CategoryRow>(`/misc-categories/${id}`, { method: 'PATCH', body }),
};

/** 800 → "8", 1250 → "12.5". What the rates inputs show. */
export function pctFromX100(x100: number): string {
  return (x100 / 100).toString();
}

/** "8" → 800, "12.5" → 1250. Rounded to hundredths; the Worker refuses anything else. */
export function pctToX100(text: string): number | null {
  const n = Number(text);
  if (!Number.isFinite(n) || n < 0 || n > 100) return null;
  return Math.round(n * 100);
}
